import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IconButton, ListItemIcon, MenuItem, Tooltip, Typography } from "@mui/material";
import IconifyIcon from "src/@core/components/icon";
import { AsyncSelect, ControlledDatePicker, ControlledTextField } from 'src/components/forms';
import { ControlledAutocomplete } from "src/components/forms/ControlledAutocomplete";
import { EntityWithIdAndDescription, EntityWithIdAndDescriptionFields } from "src/types";
import { dateFormatter } from "src/utils/formatters";
import { HttpSupplierOrders } from "../api/HttpSupplierOrders";
import {
    SupplierOrderInsertDTO,
    SupplierOrderUpdateDTO,
    SupplierOrderViewDTO,
    schemaSupplierOrderInsert,
    schemaSupplierOrderUpdate
} from "../types/dto";
import { SupplierOrderStatusTypes } from "../types/enum";
import { SupplierOrderFields } from "../types/fields";
import { getSupplierOrderStatusColor } from "../types/typification";
import SupplierOrderHistoryDialog from "./SupplierOrderHistoryDialog";


type DialogMode = 'create' | 'edit' | 'view';

interface SupplierOrderDialogProps {
    open: boolean;
    onClose: () => void;
    onAfterSubmit: () => void;
    order?: SupplierOrderViewDTO;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const SupplierOrderDialog = ({ open, onClose, onAfterSubmit, order, maxWidth = 'md' }: SupplierOrderDialogProps) => {
    const [historyOpen, setHistoryOpen] = useState(false);
    const [mode, setMode] = useState<DialogMode>(() => order ? 'view' : 'create');
    const isViewMode = mode === 'view';
    const isEdit = mode === 'edit';

    const { control, handleSubmit, reset, setValue } = useForm<SupplierOrderInsertDTO | SupplierOrderUpdateDTO>({
        resolver: zodResolver(isEdit ? schemaSupplierOrderUpdate : schemaSupplierOrderInsert),
        defaultValues: {
            [SupplierOrderFields.Items]: '',
            [SupplierOrderFields.DestinationClient]: '',
            [SupplierOrderFields.StatusCode]: SupplierOrderStatusTypes.PENDING_ORDER,
        }
    });

    useEffect(() => {
        if (order) {
            setValue(SupplierOrderFields.Items, order[SupplierOrderFields.Items]);
            setValue(SupplierOrderFields.DestinationClient, order[SupplierOrderFields.DestinationClient]);
            setValue(SupplierOrderFields.ProviderId, order[SupplierOrderFields.ProviderId]);
            setValue(SupplierOrderFields.Transport, order[SupplierOrderFields.Transport]);
            setValue(SupplierOrderFields.ResponsibleUserId, order[SupplierOrderFields.ResponsibleUserId]);
            setValue(SupplierOrderFields.CommunicationChannelCode, order[SupplierOrderFields.CommunicationChannelCode]);
            setValue(SupplierOrderFields.EstimatedAmount, order[SupplierOrderFields.EstimatedAmount]?.toString());

            setValue(SupplierOrderFields.DeliveryDate, order[SupplierOrderFields.DeliveryDate]);
            setValue(SupplierOrderFields.RequestDate, order[SupplierOrderFields.RequestDate]);

            if (isEdit) {
                setValue(SupplierOrderFields.StatusCode, order[SupplierOrderFields.StatusCode]);
            }
        } else {
            reset();
        }
    }, [order, setValue, reset, isEdit]);

    const onSubmit = (data: SupplierOrderInsertDTO | SupplierOrderUpdateDTO) => {
        const promise = isEdit
            ? HttpSupplierOrders.put(order!.id, data as SupplierOrderUpdateDTO)
            : HttpSupplierOrders.post(data as SupplierOrderInsertDTO);

        toast.promise(promise, {
            loading: isEdit ? 'Actualizando...' : 'Creando...',
            success: isEdit ? 'Pedido actualizado' : 'Pedido creado exitosamente',
            error: 'Ocurrió un error al guardar'
        }).then(() => {
            onAfterSubmit();
        });
    };

    const handleModeChange = (newMode: DialogMode) => {
        setMode(newMode);
        if (newMode === 'view') {
            reset({
                ...order,
                [SupplierOrderFields.EstimatedAmount]: order?.[SupplierOrderFields.EstimatedAmount]?.toString(),
            });
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
                <DialogTitle sx={{ mb: -2 }}>
                    {isViewMode ? 'Detalle de Pedido' : isEdit ? `Editar Pedido #${order?.id}` : 'Nuevo Pedido a Proveedor'}
                    {order && (
                        !isEdit ? (
                            <Tooltip title="Editar">
                                <IconButton
                                    color={'primary'}
                                    onClick={() => handleModeChange('edit')}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Ver">
                                <IconButton
                                    color={'primary'}
                                    onClick={() => handleModeChange('view')}>
                                    <VisibilityIcon />
                                </IconButton>
                            </Tooltip>
                        )
                    )}
                    {
                        order &&
                        <Tooltip title="Ver Historial">
                            <IconButton onClick={() => setHistoryOpen(true)}>
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>
                    }
                    <Typography variant="body2">
                        {`Fecha de carga: ${dateFormatter.toShortDate(order?.[SupplierOrderFields.CreatedDate] || new Date())}`}
                    </Typography>
                </DialogTitle>

                <DialogContent dividers>
                    <Grid container spacing={3} sx={{ mt: -2 }}>
                        <Grid item xs={12}>
                            <ControlledTextField
                                control={control}
                                name={SupplierOrderFields.Items}
                                label="Detalle del Pedido / Mercadería"
                                multiline
                                rows={3}
                                fullWidth
                                required
                                disabled={isViewMode}
                            />
                        </Grid>

                        <Grid item md={6} xs={12}>
                            <ControlledTextField
                                control={control}
                                name={SupplierOrderFields.DestinationClient}
                                label="Cliente Destino"
                                fullWidth
                                required
                                disabled={isViewMode}
                            />
                        </Grid>

                        <Grid item md={6} xs={12}>
                            <ControlledTextField
                                control={control}
                                name={SupplierOrderFields.Transport}
                                label="Transporte de Envío"
                                fullWidth
                                disabled={isViewMode}
                            />
                        </Grid>


                        <Grid item container xs={12} spacing={3}>
                            <Grid item md={6} xs={12}>
                                <AsyncSelect
                                    control={control}
                                    name={SupplierOrderFields.ProviderId}
                                    label="Proveedor"
                                    loadOptions={HttpSupplierOrders.getProviders}
                                    fullWidth
                                    disabled={isViewMode}
                                />
                            </Grid>

                            <Grid item md={6} xs={12}>
                                <AsyncSelect
                                    control={control}
                                    name={SupplierOrderFields.CommunicationChannelCode}
                                    label="Canal Comunicación"
                                    loadOptions={HttpSupplierOrders.getCommunicationChannels}
                                    fullWidth
                                    disabled={isViewMode}
                                />
                            </Grid>
                        </Grid>

                        <Grid item container xs={12} spacing={3}>
                            <Grid item md={6} xs={12}>
                                <AsyncSelect
                                    control={control}
                                    name={SupplierOrderFields.ResponsibleUserId}
                                    label="Responsable"
                                    loadOptions={HttpSupplierOrders.getUsers}
                                    fullWidth
                                    disabled={isViewMode}
                                />
                            </Grid>

                            <Grid item md={6} xs={12}>
                                <ControlledTextField control={control} name={SupplierOrderFields.EstimatedAmount} label={'Importe Estimado'}
                                    fullWidth currency prefix={'$'} disabled={isViewMode} />
                            </Grid>
                        </Grid>


                        <Grid item md={6} xs={12}>
                            <ControlledDatePicker
                                control={control}
                                name={SupplierOrderFields.RequestDate}
                                label="Fecha Pedido"
                                fullWidth
                                disabled={isViewMode}
                            />
                        </Grid>

                        <Grid item md={6} xs={12}>
                            <ControlledDatePicker
                                control={control}
                                name={SupplierOrderFields.DeliveryDate}
                                label="Fecha Entrega"
                                fullWidth
                                disabled={isViewMode}
                            />
                        </Grid>

                        {(isEdit || isViewMode) && (
                            <Grid item xs={12}>
                                <ControlledAutocomplete
                                    loadOptions={HttpSupplierOrders.getStates}
                                    control={control}
                                    name={SupplierOrderFields.StatusCode}
                                    label={'Estado del Pedido'}
                                    valueField={'id'}
                                    renderOption={(renderProps, value: EntityWithIdAndDescription, state) => {
                                        const color = getSupplierOrderStatusColor(value[EntityWithIdAndDescriptionFields.Id])

                                        return (
                                            <MenuItem key={state.index} selected={state.selected} value={value.id} {...renderProps}>
                                                <ListItemIcon>
                                                    <IconifyIcon icon='mdi:circle' color={color} fontSize='1rem' />
                                                </ListItemIcon>
                                                {value.descripcion}
                                            </MenuItem>
                                        )
                                    }}
                                    disabled={isViewMode}
                                />
                            </Grid>
                        )}

                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={onClose} color="inherit">
                        Cancelar
                    </Button>
                    {!isViewMode && (
                        <Button
                            variant="contained"
                            onClick={handleSubmit(onSubmit)}
                        >
                            Guardar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            {order && (
                <SupplierOrderHistoryDialog
                    open={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                    orderId={order.id}
                />
            )}
        </>
    );
};

export default SupplierOrderDialog;
